
































































































import { UserApi } from '@/api/user';
import loading from '@/components/loading.vue';
import addUserDialog from '@/components/settings/users/add-user-dialog.vue';
import assignUserToPartnerPracticeDialog from '@/components/settings/users/assign-user-to-partner-practice-dialog.vue';
import editUserDialog from '@/components/settings/users/edit-user-dialog.vue';
import usersTable from '@/components/settings/users/users-table.vue';
import { PartnerPractice } from '@/shared/partner-practice';
import { RoleName } from '@/shared/role-name';
import { User } from '@/shared/user';
import { UserStatus } from '@/shared/user-status';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const userModule = namespace('user');
const usersModule = namespace('users');
const userApi = new UserApi();

@Component({
    components: {
        loading,
        addUserDialog,
        assignUserToPartnerPracticeDialog,
        editUserDialog,
        usersTable,
    },
})
export default class Users extends Vue {
    @partnerPracticesModule.Getter('partnerPractices') private partnerPractices!: Array<PartnerPractice>;

    @userModule.Getter('user') private currentUser!: User | null;
    @userModule.Getter('isAdministrator') public isAdministrator!: boolean;

    @usersModule.Getter('rightLegalGroupUsers') private rightLegalGroupUsers!: (status: UserStatus) => Array<User>;

    private isLoading: boolean = true;
    private users: Array<User> = new Array<User>();
    private selectedUser: User | null = null;
    private statusFilter: number = 1;
    private addUserDialogVisible: boolean = false;
    private editUserDialogVisible: boolean = false;
    private assignToPartnerPracticeDialogVisible: boolean = false;
    private resendActivationConfirmationDialogVisible: boolean = false;
    private restoreConfirmationDialogVisible: boolean = false;
    private archiveConfirmationDialogVisible: boolean = false;
    private statuses: Array<{ id: UserStatus, value: string }> = [
        { id: UserStatus.All, value: 'All' },
        { id: UserStatus.Active, value: 'Active' },
        { id: UserStatus.Archived, value: 'Archived' },
    ];

    @Watch('statusFilter')
    private async onStatusFilterChangedAsync(): Promise<void> {
        await this.fetchUsersAsync();
    }

    private async fetchUsersAsync(): Promise<void> {
        this.isLoading = true;

        await this.$store.dispatch(`users/loadAllUsersAsync`);
        this.users = this.rightLegalGroupUsers(this.statusFilter);

        this.isLoading = false;
    }

    private async onUserCreatedAsync(user: User): Promise<void> {
        this.$snackbar.open({ message: `User '${user.email}' has been created.`, type: 'is-success' });

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private async onUserUpdatedAsync(user: User): Promise<void> {
        this.$snackbar.open({ message: `User '${user.email}' has been updated.`, type: 'is-success' });

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private async onUserAssignedToPartnerPracticeAsync(user: User): Promise<void> {
        const partnerPractice = this.partnerPractices.find((pp) => pp.id === user.partnerPracticeId);
        const message = partnerPractice == null
            ? `User '${user.email}' assigned to partner practice.`
            : `User '${user.email}' assigned to partner practice '${partnerPractice.name}'.`;

        this.$snackbar.open({ message, type: 'is-success' });

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private async onResendActivationConfirmedAsync(): Promise<void> {
        try {
            await userApi.reinviteUserAsync(this.selectedUser!);
            this.$snackbar.open({ message: `Activation e-mail sent to user '${this.selectedUser!.email}'.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not re-send activation e-mail to user '${this.selectedUser!.email}'.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private async onRestoreUserConfirmedAsync(): Promise<void> {
        try {
            await userApi.updateUserAsync({
                ...this.selectedUser!,
                id: this.selectedUser!.id!,
                isArchived: false,
                roles: this.selectedUser!.roles.map((role) => role.name),
                version: this.selectedUser!.version!,
            });

            this.$snackbar.open({ message: `User '${this.selectedUser!.email}' has been restored.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not restore user '${this.selectedUser!.email}'.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private async onArchiveUserConfirmedAsync(): Promise<void> {
        try {
            await userApi.deleteUserAsync(this.selectedUser!);
            this.$snackbar.open({ message: `User '${this.selectedUser!.email}' has been archived.`, type: 'is-success' });
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: `Could not archive user '${this.selectedUser!.email}'.`, type: 'is-danger' });
            }
        }

        this.onCancel();
        await this.fetchUsersAsync();
    }

    private onAddUser(): void {
        this.selectedUser = null;
        this.addUserDialogVisible = true;
    }

    private onEditUser(user: User): void {
        this.selectedUser = { ...user, roles: [...user.roles] };
        this.editUserDialogVisible = true;
    }

    private onAssignToPartnerPractice(user: User): void {
        this.selectedUser = {
            ...user,
            roles: [...user.roles.filter((role) =>
                role.name.toLowerCase() === RoleName.LegalAdvisor.toLowerCase() ||
                role.name.toLowerCase() === RoleName.LegalAssistant.toLowerCase() ||
                role.name.toLowerCase() === RoleName.HoD.toLowerCase())],
        };
        this.assignToPartnerPracticeDialogVisible = true;
    }

    private onResendActivation(user: User): void {
        this.selectedUser = { ...user, roles: [...user.roles] };
        this.resendActivationConfirmationDialogVisible = true;
    }

    private onRestoreUser(user: User): void {
        this.selectedUser = { ...user, roles: [...user.roles] };
        this.restoreConfirmationDialogVisible = true;
    }

    private onArchiveUser(user: User): void {
        this.selectedUser = { ...user, roles: [...user.roles] };
        this.archiveConfirmationDialogVisible = true;
    }

    private onCancel(): void {
        this.addUserDialogVisible = false;
        this.editUserDialogVisible = false;
        this.assignToPartnerPracticeDialogVisible = false;
        this.resendActivationConfirmationDialogVisible = false;
        this.restoreConfirmationDialogVisible = false;
        this.archiveConfirmationDialogVisible = false;

        this.selectedUser = null;
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadPartnerPracticeTypesAsync`),
            this.$store.dispatch(`users/loadRolesAsync`),
        ]);

        await this.fetchUsersAsync();

        this.isLoading = false;
    }
}
